import * as React from "react";
import { ConversationProvider, useConversation } from "@elevenlabs/react";

import useSyncConversationCustomer from "../../hooks/useSyncConversationCustomer";
import { SessionEndedError, startChatSession } from "../../utils/elevenlabsSession";
import { cn } from "../ui/utils";
import BothConversationView from "./ui/both-conversation-view";
import TextConversationView from "./ui/text-conversation-view";
import VoiceConversationView from "./ui/voice-conversation-view";

export type ConversationMode = "both" | "voice" | "text";
export type AgentState =
  | "disconnected"
  | "connecting"
  | "connected"
  | "disconnecting"
  | null;

export type ElevenLabsSocketMessage = {
  source: "user" | "ai";
  message: string;
};

export interface RealtimeConversationBarProps {
  avatar: string;
  allocatedUserName?: string;
  agentId: string | undefined;
  customerId?: number | null;
  mode?: ConversationMode;
  keyboardOpen: boolean;
  setKeyboardOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  dynamicVariables?: Record<string, string | number | boolean>;
  className?: string;
  waveformClassName?: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
  onMessage?: (message: ElevenLabsSocketMessage) => void;
  onSendMessage?: (message: string) => void;
  debug?: boolean;
  autoStart?: boolean;
  agentVoiceId?: string;
}

function safeError(err: unknown) {
  if (err instanceof Error) {
    return { name: err.name, message: err.message, stack: err.stack };
  }
  try {
    return { message: JSON.stringify(err) };
  } catch {
    return { message: String(err) };
  }
}

function isElevenLabsSocketMessage(x: unknown): x is ElevenLabsSocketMessage {
  if (!x || typeof x !== "object") return false;
  const m = x as Record<string, unknown>;
  return (
    (m.source === "user" || m.source === "ai") && typeof m.message === "string"
  );
}

function stopMediaStream(stream: MediaStream | null) {
  if (!stream) return;
  stream.getTracks().forEach((t) => t.stop());
}

const RealtimeConversationBarInner = React.forwardRef<
  HTMLDivElement,
  RealtimeConversationBarProps
>(
  (
    {
      avatar,
      allocatedUserName,
      agentId,
      customerId,
      mode = "both",
      keyboardOpen,
      setKeyboardOpen,
      dynamicVariables,
      className,
      waveformClassName,
      onConnect,
      onDisconnect,
      onError,
      onMessage,
      onSendMessage,
      debug = false,
      autoStart = false,
      agentVoiceId,
    },
    ref,
  ) => {
    const debugLog = React.useCallback(
      (...args: unknown[]) => {
        if (!debug) return;
        const ts = new Date().toISOString();
        // eslint-disable-next-line no-console
        console.log(`[E11][${ts}]`, ...args);
      },
      [debug],
    );

    const [isMuted, setIsMuted] = React.useState(false);
    const [agentState, setAgentState] =
      React.useState<AgentState>("disconnected");
    const [textInput, setTextInput] = React.useState("");

    const mediaStreamRef = React.useRef<MediaStream | null>(null);
    const [micStream, setMicStream] = React.useState<MediaStream | null>(null);
    const isUnmountedRef = React.useRef(false);
    const [sessionConversationId, setSessionConversationId] =
      React.useState<string | null>(null);
    const syncedConversationIdRef = React.useRef<string | null>(null);
    const attemptedSyncKeyRef = React.useRef<string | null>(null);
    // the SDK also reports a failed start to onError; startConversation handles (and retries) those
    const startingRef = React.useRef(false);
    const { syncConversationCustomer } = useSyncConversationCustomer();

    const isTextOnlyMode = mode === "text";
    const isConnected = agentState === "connected";

    React.useEffect(() => {
      debugLog("mounted", { mode, isTextOnlyMode, agentIdPresent: !!agentId });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const conversation = useConversation({
      textOnly: isTextOnlyMode,
      overrides: {
        conversation: {
          textOnly: isTextOnlyMode,
        },
      },
      ...(isTextOnlyMode ? {} : { micMuted: isMuted }),

      onConnect: () => {
        if (isUnmountedRef.current) {
          debugLog("sdk:onConnect() ignored (unmounted)");
          return;
        }
        debugLog("sdk:onConnect()");
        setAgentState("connected");
        onConnect?.();
      },
      onDisconnect: () => {
        if (isUnmountedRef.current) {
          debugLog("sdk:onDisconnect() ignored (unmounted)");
          return;
        }
        debugLog("sdk:onDisconnect()");
        setAgentState("disconnected");
        setSessionConversationId(null);
        syncedConversationIdRef.current = null;
        attemptedSyncKeyRef.current = null;
        onDisconnect?.();
        if (mode !== "text") setKeyboardOpen(false);
        stopMediaStream(mediaStreamRef.current);
        mediaStreamRef.current = null;
        setMicStream(null);
      },
      onMessage: (message: unknown) => {
        if (isUnmountedRef.current) {
          debugLog("sdk:onMessage() ignored (unmounted)");
          return;
        }
        debugLog("sdk:onMessage()", message);
        if (isElevenLabsSocketMessage(message)) {
          onMessage?.(message);
        }
      },
      onError: (error: unknown) => {
        if (startingRef.current) return;
        if (isUnmountedRef.current) {
          debugLog("sdk:onError() ignored (unmounted)", safeError(error));
          return;
        }
        const e = safeError(error);
        debugLog("sdk:onError()", e);
        setAgentState("disconnected");
        stopMediaStream(mediaStreamRef.current);
        mediaStreamRef.current = null;
        setMicStream(null);

        const errorObj =
          error instanceof Error
            ? error
            : new Error(
                typeof error === "string" ? error : JSON.stringify(error),
              );
        onError?.(errorObj);
      },
    });

    const syncCustomerConversation = React.useCallback(
      async (conversationId: string) => {
        if (
          typeof customerId !== "number" ||
          !Number.isFinite(customerId) ||
          customerId <= 0
        ) {
          return;
        }

        const syncKey = `${customerId}:${conversationId}`;
        if (attemptedSyncKeyRef.current === syncKey) return;
        if (syncedConversationIdRef.current === conversationId) return;

        debugLog("syncConversationCustomer()", {
          customerId,
          conversationId,
        });
        try {
          attemptedSyncKeyRef.current = syncKey;
          await syncConversationCustomer({
            customer_id: customerId,
            conversation_id: conversationId,
          });
          syncedConversationIdRef.current = conversationId;
        } catch (error) {
          debugLog("syncConversationCustomer(): failed", safeError(error));
        }
      },
      [customerId, syncConversationCustomer, debugLog],
    );

    React.useEffect(() => {
      if (!sessionConversationId) return;
      if (syncedConversationIdRef.current === sessionConversationId) return;

      void syncCustomerConversation(sessionConversationId);
    }, [sessionConversationId, syncCustomerConversation]);

    const getMicStream = React.useCallback(async () => {
      if (mediaStreamRef.current) return mediaStreamRef.current;

      debugLog("requesting microphone permission...");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      setMicStream(stream);
      debugLog("microphone stream acquired");
      return stream;
    }, [debugLog]);

    const startConversation = React.useCallback(
      async (withVoice: boolean = true) => {
        const connectionType = withVoice ? "webrtc" : "websocket";

        debugLog("startConversation()", {
          withVoice,
          connectionType,
          agentId,
          dynamicVariablesPresent: !!dynamicVariables,
        });

        try {
          if (!agentId) {
            onError?.(new Error("ElevenLabs Agent ID not configured."));
            return;
          }

          setAgentState("connecting");

          if (withVoice) {
            await getMicStream();
            setIsMuted(false);
          } else {
            setIsMuted(false);
            setKeyboardOpen(true);
          }

          debugLog("calling startSession()", {
            agentId,
            connectionType,
            dynamicVariables,
          });

          startingRef.current = true;
          const startedConversationId = await startChatSession(conversation, {
            agentId,
            voice: withVoice,
            textOnly: isTextOnlyMode,
            history: [],
            voiceId: agentVoiceId,
            dynamicVariables,
            onStatusChange: (status: unknown) => {
              debugLog("sdk:onStatusChange()", status);
              if (status && typeof status === "object" && "status" in status) {
                const next = (status as { status?: AgentState }).status ?? null;
                setAgentState(next);
              }
            },
          });

          debugLog("startSession() resolved", {
            conversationId: startedConversationId,
          });
          setSessionConversationId(startedConversationId || null);
        } catch (err) {
          const e = safeError(err);
          debugLog("startConversation(): failed", e);
          if (isUnmountedRef.current) {
            debugLog("startConversation(): failure ignored (unmounted)");
            return;
          }
          setAgentState("disconnected");
          if (!(err instanceof SessionEndedError)) onError?.(err as Error);
        } finally {
          startingRef.current = false;
        }
      },
      [
        agentId,
        agentVoiceId,
        conversation,
        dynamicVariables,
        isTextOnlyMode,
        getMicStream,
        onError,
        setKeyboardOpen,
        debugLog,
      ],
    );

    const handleEndSession = React.useCallback(() => {
      debugLog("handleEndSession()");
      try {
        conversation.endSession();
      } catch {}
      setAgentState("disconnected");
      setSessionConversationId(null);
      syncedConversationIdRef.current = null;
      attemptedSyncKeyRef.current = null;

      stopMediaStream(mediaStreamRef.current);
      mediaStreamRef.current = null;
      setMicStream(null);

      debugLog("microphone stream stopped");
    }, [conversation, debugLog]);

    const didAutoStartRef = React.useRef(false);
    React.useEffect(() => {
      if (!autoStart) return;
      if (didAutoStartRef.current) return;
      if (!agentId) return;
      if (agentState !== "disconnected") return;

      let cancelled = false;
      const t = window.setTimeout(() => {
        if (cancelled) return;
        if (!agentId) return;
        if (agentState !== "disconnected") return;

        didAutoStartRef.current = true;
        void startConversation(mode === "voice");
      }, 0);

      return () => {
        cancelled = true;
        window.clearTimeout(t);
      };
    }, [autoStart, agentId, agentState, startConversation, mode]);

    const toggleMute = React.useCallback(() => {
      debugLog("toggleMute()", { from: isMuted, to: !isMuted });
      setIsMuted((prev) => !prev);
    }, [debugLog, isMuted]);

    const handleStartVoice = React.useCallback(() => {
      debugLog("handleStartVoice()", { agentState });
      if (agentState === "connected" || agentState === "connecting") {
        handleEndSession();
      } else if (agentState === "disconnected") {
        void startConversation(true);
      }
    }, [agentState, handleEndSession, startConversation, debugLog]);

    const handleStartChat = React.useCallback(() => {
      debugLog("handleStartChat()", { agentState });
      if (agentState === "disconnected") {
        void startConversation(false);
      }
    }, [agentState, startConversation, debugLog]);

    const handleSendText = React.useCallback(() => {
      debugLog("handleSendText()", {
        agentState,
        textLen: textInput.trim().length,
      });
      if (!textInput.trim() || agentState !== "connected") return;

      const messageToSend = textInput;
      debugLog("sendUserMessage()", { messageToSend });
      conversation.sendUserMessage(messageToSend);
      setTextInput("");
      onSendMessage?.(messageToSend);
    }, [conversation, textInput, onSendMessage, agentState, debugLog]);

    const handleTextChange = React.useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setTextInput(value);

        if (isConnected) {
          conversation.sendUserActivity?.();
        }
      },
      [conversation, isConnected],
    );

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleSendText();
        }
      },
      [handleSendText],
    );

    React.useEffect(() => {
      return () => {
        isUnmountedRef.current = true;
        debugLog("unmount cleanup");
        stopMediaStream(mediaStreamRef.current);
        mediaStreamRef.current = null;
        // ConversationProvider ends the session itself on unmount
      };
    }, [debugLog]);

    if (mode === "voice") {
      const startDisabled = false;
      const endDisabled =
        agentState === "disconnected" || agentState === "disconnecting";

      const onPrimaryClick = () => {
        if (agentState === "disconnected") void startConversation(true);
        else if (isConnected) toggleMute();
      };

      return (
        <div ref={ref} className={cn("w-full h-full", className)}>
          <VoiceConversationView
            allocatedUserName={allocatedUserName}
            agentState={agentState}
            isConnected={isConnected}
            isMuted={isMuted}
            micStream={micStream}
            avatar={avatar}
            onPrimaryClick={onPrimaryClick}
            onEnd={handleEndSession}
            startDisabled={startDisabled}
            endDisabled={endDisabled}
          />
        </div>
      );
    }

    if (mode === "text") {
      const endDisabled =
        agentState === "disconnected" || agentState === "disconnecting";

      return (
        <div ref={ref} className={cn("w-full", className)}>
          <TextConversationView
            className="gap-1.5"
            isConnected={isConnected}
            textInput={textInput}
            onTextChange={handleTextChange}
            onKeyDown={handleKeyDown}
            onSend={handleSendText}
            onEnd={handleEndSession}
            endDisabled={endDisabled}
          />
        </div>
      );
    }

    return (
      <div ref={ref} className={cn("w-full", className)}>
        <BothConversationView
          className={cn("w-full", className)}
          keyboardOpen={keyboardOpen}
          setKeyboardOpen={setKeyboardOpen}
          waveformClassName={waveformClassName}
          micStream={micStream}
          agentState={agentState}
          isConnected={isConnected}
          isMuted={isMuted}
          onToggleMute={toggleMute}
          onEnd={handleEndSession}
          onStartChat={handleStartChat}
          onStartVoice={handleStartVoice}
          textInput={textInput}
          onTextChange={handleTextChange}
          onKeyDown={handleKeyDown}
          onSend={handleSendText}
        />
      </div>
    );
  },
);

RealtimeConversationBarInner.displayName = "RealtimeConversationBarInner";

// @elevenlabs/react 1.x keeps the session in a provider; one per bar so chats never share it.
export const RealtimeConversationBar = React.forwardRef<
  HTMLDivElement,
  RealtimeConversationBarProps
>((props, ref) => (
  <ConversationProvider>
    <RealtimeConversationBarInner {...props} ref={ref} />
  </ConversationProvider>
));

RealtimeConversationBar.displayName = "RealtimeConversationBar";

export default RealtimeConversationBar;
